import { Suspense, type ReactNode } from "react";
import {
  Box,
  Center,
  Hide,
  Icon,
  Show,
  useBreakpointValue,
} from "@chakra-ui/react";
import { Outlet } from "react-router-dom";
import TopBarProgress from "react-topbar-progress-indicator";
import PropertyIcon from "assets/icons/medium/property.svg?react";
import { PageRenderListener } from "components/page-render-listener";
import { PortalSelector } from "components/portal-selector";
import {
  getTopNavigationBarHeight,
  TopNavigationBar,
} from "components/top-navigation-bar";
import { usePortalPath } from "hooks/use-portal-path";

type AppLayoutProps = {
  leftNavigationMenu: ReactNode;
  hideListingSelector?: boolean;
  showPreviewBanner?: boolean;
};

TopBarProgress.config({
  barColors: {
    "0": "#fff",
    "1.0": "#fff",
  },
  shadowBlur: 5,
});

export function AppLayout({
  leftNavigationMenu,
  showPreviewBanner = false,
  hideListingSelector = showPreviewBanner,
}: AppLayoutProps) {
  const margin = useBreakpointValue(
    { base: "0px", lg: "320px" },
    { ssr: false },
  );

  const { isLandlordOrTenantPath } = usePortalPath();

  return (
    <Box>
      <TopNavigationBar
        leftNavigationMenu={leftNavigationMenu}
        showPreviewBanner={showPreviewBanner}
      />
      <Box display={"flex"} overflowX={"hidden"} h={"100vh"}>
        <Show above={"lg"}>{leftNavigationMenu}</Show>
        <Box
          w={"100%"}
          overflowX={"hidden"}
          ml={margin}
          mt={getTopNavigationBarHeight()}
          p={{ base: 0, md: 10 }}
          pt={{ sm: 0 }} // No top padding when showing portal selector
        >
          {/* Mobile Only: Portal Selector */}
          {!hideListingSelector && !isLandlordOrTenantPath && (
            <Hide above={"sm"}>
              <Box borderBottomWidth={"1px"} borderBottomColor={"gray.100"}>
                <Center>
                  <PortalSelector
                    prefixIcon={<Icon as={PropertyIcon} boxSize={"12px"} />}
                  />
                </Center>
              </Box>
            </Hide>
          )}
          <Suspense fallback={<TopBarProgress />}>
            <Outlet />
          </Suspense>
          <PageRenderListener />
        </Box>
      </Box>
    </Box>
  );
}
